/**
 * ViewBuilderStepper - Navigation component for the 5-step view builder
 */

import React from "react";
import {
  Box,
  Stepper,
  Step,
  StepButton,
  Typography,
  Button,
  Stack,
  Alert,
} from "@mui/joy";
import CheckIcon from "@mui/icons-material/Check";
import TableChartIcon from "@mui/icons-material/TableChart";
import LinkIcon from "@mui/icons-material/Link";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FilterListIcon from "@mui/icons-material/FilterList";
import SecurityIcon from "@mui/icons-material/Security";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useViewBuilderContext } from "./ViewBuilderContext";
import { VIEW_BUILDER_STEPS } from "../../types";

interface StepConfig {
  id: string;
  label: string;
  icon: React.ReactNode;
  validate: () => boolean;
}

interface ViewBuilderStepperProps {
  onSave: () => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

export default function ViewBuilderStepper(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _props: ViewBuilderStepperProps,
) {
  const {
    state,
    setStep,
    setError,
    validateCurrentStep: contextValidateCurrentStep,
    clearAllValidationErrors,
  } = useViewBuilderContext();

  const stepConfigs: StepConfig[] = [
    {
      id: "focus",
      label: "Focus Table",
      icon: <TableChartIcon fontSize="small" />,
      validate: () => !!state.name.trim() && !!state.focusTableId,
    },
    {
      id: "joins",
      label: "Joins",
      icon: <LinkIcon fontSize="small" />,
      validate: () => true, // Joins are optional
    },
    {
      id: "fields",
      label: "Visible Fields",
      icon: <ViewColumnIcon fontSize="small" />,
      validate: () => state.selectedFields.length > 0,
    },
    {
      id: "filters",
      label: "Filters",
      icon: <FilterListIcon fontSize="small" />,
      validate: () => state.filters.every((f) => f.columnId && f.operator),
    },
    {
      id: "permissions",
      label: "Permissions",
      icon: <SecurityIcon fontSize="small" />,
      validate: () => true, // Permissions are optional
    },
  ];

  const handleStepClick = (index: number) => {
    // Validate current step before allowing navigation backwards
    if (index < state.currentStep) {
      clearAllValidationErrors();
      setError(null);
      setStep(index);
      return;
    }

    // Validate before navigating forward - use context validation for per-field errors
    const isValid = contextValidateCurrentStep();
    if (!isValid) {
      setError(getValidationError(state.currentStep));
      return;
    }

    clearAllValidationErrors();
    setError(null);
    setStep(index);
  };

  const getValidationError = (stepIndex: number): string => {
    switch (stepIndex) {
      case 0:
        if (!state.name.trim()) return "View name is required";
        if (!state.focusTableId) return "Please select a focus table";
        return "";
      case 2:
        if (state.selectedFields.length === 0)
          return "Please select at least one field";
        return "";
      case 3:
        const invalidFilter = state.filters.find(
          (f) => !f.columnId || !f.operator,
        );
        if (invalidFilter) return "Please complete all filter configurations";
        return "";
      default:
        return "";
    }
  };

  const isStepComplete = (index: number): boolean => {
    return index < state.currentStep && stepConfigs[index]?.validate();
  };

  return (
    <Box>
      {/* Stepper Navigation */}
      <Stepper
        sx={{
          mb: 3,
          "--Stepper-verticalGap": "0",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {stepConfigs.map((step, idx) => (
          <Step
            key={step.id}
            completed={isStepComplete(idx)}
            indicator={
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  minWidth: 32,
                  minHeight: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  bgcolor:
                    idx === state.currentStep
                      ? "primary.500"
                      : isStepComplete(idx)
                        ? "success.500"
                        : "neutral.400",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "sm",
                  transition: "all 0.2s ease",
                }}
              >
                {isStepComplete(idx) ? (
                  <CheckIcon fontSize="small" />
                ) : (
                  step.icon
                )}
              </Box>
            }
          >
            <StepButton
              onClick={() => handleStepClick(idx)}
              sx={{
                flexDirection: "row",
                gap: 1.5,
                py: 1,
                px: 2,
                borderRadius: "md",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "transparent",
                },
              }}
            >
              <Typography
                level="body-sm"
                sx={{
                  fontWeight: idx === state.currentStep ? "lg" : "md",
                  color:
                    idx === state.currentStep
                      ? "primary.600"
                      : "text.secondary",
                }}
              >
                {step.label}
              </Typography>
            </StepButton>
          </Step>
        ))}
      </Stepper>

      {/* Error Display */}
      {state.error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {state.error}
        </Alert>
      )}

      {/* Step Description */}
      <Typography level="body-sm" sx={{ color: "text.tertiary", mb: 2 }}>
        {VIEW_BUILDER_STEPS[state.currentStep]?.description}
      </Typography>
    </Box>
  );
}

/**
 * Navigation buttons for the view builder
 */
interface ViewBuilderNavigationProps {
  onSave: () => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

export function ViewBuilderNavigation({
  onSave,
  onCancel,
  isSaving,
}: ViewBuilderNavigationProps) {
  const {
    state,
    nextStep,
    prevStep,
    setError,
    setStep,
    validateCurrentStep: contextValidateCurrentStep,
    clearAllValidationErrors,
  } = useViewBuilderContext();

  const stepConfigs = [
    { validate: () => !!state.name.trim() && !!state.focusTableId },
    { validate: () => true },
    { validate: () => state.selectedFields.length > 0 },
    { validate: () => state.filters.every((f) => f.columnId && f.operator) },
    { validate: () => true },
  ];

  const handleNext = () => {
    // Use context validation for per-field errors
    const isValid = contextValidateCurrentStep();
    if (!isValid) {
      setError(getValidationError(state.currentStep));
      return;
    }
    clearAllValidationErrors();
    setError(null);
    nextStep();
  };

  const handlePrev = () => {
    clearAllValidationErrors();
    setError(null);
    prevStep();
  };

  const handleSave = async () => {
    if (!state.name.trim()) {
      setError("View name is required");
      setStep(0);
      return;
    }
    if (!state.focusTableId) {
      setError("Please select a focus table");
      setStep(0);
      return;
    }
    if (state.selectedFields.length === 0) {
      setError("Please select at least one field");
      setStep(2);
      return;
    }
    setError(null);
    await onSave();
  };

  const getValidationError = (stepIndex: number): string => {
    switch (stepIndex) {
      case 0:
        if (!state.name.trim()) return "View name is required";
        if (!state.focusTableId) return "Please select a focus table";
        return "";
      case 2:
        return "Please select at least one field";
      case 3:
        return "Please complete all filter configurations";
      default:
        return "";
    }
  };

  const isLastStep = state.currentStep === 4;

  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="space-between"
      sx={{ mt: 3 }}
    >
      <Button variant="plain" color="neutral" onClick={onCancel}>
        Cancel
      </Button>

      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          color="neutral"
          startDecorator={<ArrowBackIcon />}
          onClick={handlePrev}
          disabled={state.currentStep === 0}
        >
          Previous
        </Button>

        {isLastStep ? (
          <Button
            color="primary"
            startDecorator={<SaveIcon />}
            onClick={handleSave}
            loading={isSaving}
          >
            {state.isEditing ? "Update View" : "Create View"}
          </Button>
        ) : (
          <Button
            color="primary"
            endDecorator={<ArrowForwardIcon />}
            onClick={handleNext}
          >
            Next
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
